/**
 * The ONE line-style picker — a compact dropdown whose trigger and options are
 * SVG dash-pattern previews (solid / dotted / dashed / large-dashed / sparse-
 * dotted), so the choice is visual rather than text. Used everywhere a line
 * style is chosen: the tool settings panel (FieldList) and the per-drawing
 * floating toolbar, so they stay identical.
 *
 * The popover is portalled to <body> with fixed positioning (and flips above
 * the trigger near the viewport bottom) so it's never clipped by a scrolling
 * settings-panel body or the floating toolbar.
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { LineStyleName } from "@/state/slices/uiSlice";
import { svgDasharrayFromName } from "@/components/Chart/priceLineStyles";
import { LINE_STYLE_OPTIONS } from "./lineStyle";
import styles from "./LineStyleSelect.module.css";

function Preview({ value, color }: { value: LineStyleName; color: string }) {
  return (
    <svg className={styles.swatch} viewBox="0 0 72 10" aria-hidden>
      <line
        x1="2"
        y1="5"
        x2="70"
        y2="5"
        stroke={color}
        strokeWidth={1}
        strokeDasharray={svgDasharrayFromName(value)}
        strokeLinecap="round"
      />
    </svg>
  );
}

interface Props {
  value: LineStyleName;
  onChange: (style: LineStyleName) => void;
  /** Preview stroke colour (default follows text colour). */
  color?: string;
  className?: string;
  /** Extra class on the trigger button — lets a host size it (e.g. the toolbar
   *  matches its 32px control height). */
  triggerClassName?: string;
}

const MENU_W = 112;
const MENU_H = 190;

export function LineStyleSelect({
  value,
  onChange,
  color = "currentColor",
  className,
  triggerClassName,
}: Props) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const below = r.bottom + 4;
    const flipUp = below + MENU_H > window.innerHeight;
    setPos({
      top: flipUp ? Math.max(4, r.top - 4 - MENU_H) : below,
      left: Math.min(r.left, window.innerWidth - MENU_W - 4),
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        triggerRef.current?.contains(t) ||
        menuRef.current?.contains(t)
      )
        return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onScroll = () => setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open]);

  return (
    <div className={`${styles.wrap}${className ? ` ${className}` : ""}`}>
      <button
        ref={triggerRef}
        type="button"
        className={`${styles.trigger}${triggerClassName ? ` ${triggerClassName}` : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Line style"
        title="Line style"
        onClick={() => setOpen((o) => !o)}
      >
        <Preview value={value} color={color} />
        <svg className={styles.caret} viewBox="0 0 10 10" aria-hidden>
          <path
            d="M2 3.5l3 3 3-3"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            className={styles.menu}
            role="listbox"
            style={{ top: pos.top, left: pos.left }}
          >
            {LINE_STYLE_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={value === o.value}
                title={o.label}
                className={`${styles.item} ${value === o.value ? styles.itemActive : ""}`}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
              >
                <Preview value={o.value} color={color} />
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}
